import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { api } from "../api.ts";
import { dateTime } from "../format.ts";
import { canEdit, useMe } from "../session.ts";
import {
  Button,
  Card,
  Empty,
  ErrorText,
  Loading,
  Notice,
  Page,
  SettingsSection,
  Table,
  Td,
} from "../ui.tsx";

/** What Claude can and can't do, said the same way on the approval page and in Settings. */
const CAN = [
  "Read your contracts and the terms recoup found in them",
  "Read your vendors, their measured uptime, credits and claim deadlines",
  "Give recoup contracts from your Claude files to read, like an upload",
];
const CANNOT = [
  "Approve terms, send or change claims, or change any setting",
  "See passwords, keys or anything in your vendors' accounts",
];

/** Where Claude sends a person to approve it: their own sign-in and two-factor, then one click. */
export function ConnectClaude() {
  const [params] = useSearchParams();
  const { data: me } = useMe();
  const request = Object.fromEntries(params);
  const [leaving, setLeaving] = useState(false);
  const client = useQuery({
    queryKey: ["oauth-client", params.toString()],
    queryFn: () =>
      api.get<{ name: string; redirectHost: string }>(`/oauth/client?${params.toString()}`),
    retry: false,
  });
  const go = (r: { redirect: string }) => {
    setLeaving(true);
    window.location.assign(r.redirect);
  };
  const allow = useMutation({
    mutationFn: () => api.post<{ redirect: string }>("/oauth/approve", request),
    onSuccess: go,
  });
  const decline = useMutation({
    mutationFn: () => api.post<{ redirect: string }>("/oauth/decline", request),
    onSuccess: go,
  });
  if (client.isLoading) return <Loading />;
  if (client.error || !client.data)
    return (
      <Page help="claude" title="Connect Claude">
        <Card>
          <ErrorText error={client.error} />
        </Card>
      </Page>
    );
  return (
    <Page
      help="claude"
      title="Connect Claude to recoup"
      lead={`${client.data.name} wants to use recoup as you, for ${me?.org?.name ?? "your company"}.`}
    >
      <Card>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <p className="mb-2 font-medium">Claude will be able to</p>
            <ul className="flex list-disc flex-col gap-1 pl-5 text-sm">
              {CAN.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="mb-2 font-medium">Claude won't be able to</p>
            <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-muted">
              {CANNOT.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
        </div>
        <p className="mt-6 text-sm text-muted">
          You'll go back to <span className="font-medium text-ink">{client.data.redirectHost}</span>
          . You can disconnect Claude at any time in Settings.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button
            variant="primary"
            busy={allow.isPending || leaving}
            onClick={() => allow.mutate()}
          >
            Allow
          </Button>
          <Button busy={decline.isPending} onClick={() => decline.mutate()}>
            Don't allow
          </Button>
        </div>
        <div className="mt-3">
          <ErrorText error={allow.error ?? decline.error} />
        </div>
      </Card>
    </Page>
  );
}

/** Settings: how to add recoup to Claude, and the connections people made. */
export function ClaudeConnections() {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const admin = canEdit(me, "admin");
  const { data } = useQuery({
    queryKey: ["claude-connections"],
    queryFn: () =>
      api.get<{
        connections: {
          id: string;
          client: string;
          user_email: string;
          created_at: string;
          last_used_at: string | null;
        }[];
      }>("/connections/claude"),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/connections/claude/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claude-connections"] }),
  });
  const url = `${window.location.origin}/mcp`;
  const [copied, setCopied] = useState(false);
  return (
    <SettingsSection
      id="claude"
      title="Claude"
      description="Ask Claude about your contracts, vendors and claims, in Cowork, claude.ai or the Claude app."
      flush
    >
      <div className="flex flex-col gap-3 px-5 py-4 text-sm">
        <p>
          In Claude, open Settings, then Connectors, and choose Add custom connector. Name it recoup
          and paste this address. Claude then sends you here to approve it.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <code className="rounded-control bg-surface-2 px-2 py-1 break-all">{url}</code>
          <Button
            variant="ghost"
            onClick={() =>
              navigator.clipboard
                .writeText(url)
                .then(() => setCopied(true))
                .catch(() => setCopied(false))
            }
          >
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <Notice tone="accent">
          Claude can read and add contracts for recoup to read. It can't approve terms, send claims
          or change settings.
        </Notice>
      </div>
      <Table
        head={["Connected by", "Connected", "Last used", ""]}
        empty={!data?.connections.length && <Empty title="Claude isn't connected yet." />}
      >
        {data?.connections.map((x) => (
          <tr key={x.id}>
            <Td>
              {x.user_email}
              <div className="text-xs text-muted">{x.client}</div>
            </Td>
            <Td>{dateTime(x.created_at)}</Td>
            <Td>{dateTime(x.last_used_at)}</Td>
            <Td className="text-right">
              {(admin || x.user_email === me?.user?.email) && (
                <Button variant="ghost" onClick={() => revoke.mutate(x.id)}>
                  Disconnect
                </Button>
              )}
            </Td>
          </tr>
        ))}
      </Table>
    </SettingsSection>
  );
}
